import { useCallback, useEffect, useRef, useState } from "react";
import type { TouchEvent as ReactTouchEvent } from "react";
import { Link } from "react-router-dom";

interface Slide {
  id: string;
  title: string;
  summary: string;
  image: string;
  tag: string;
  link: string;
}

export default function HeroCarousel({ slides }: { slides: Slide[] }) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);
  const count = slides.length;

  const goTo = useCallback(
    (next: number) => setIndex((next + count) % count),
    [count],
  );

  useEffect(() => {
    const timer = setInterval(() => setIndex((v) => (v + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  const onTouchStart = (e: ReactTouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: ReactTouchEvent) => {
    if (touchStart.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(diff) > 50) {
      goTo(diff > 0 ? index - 1 : index + 1);
    }
    touchStart.current = null;
  };

  return (
    <section
      className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点资讯配图`}
            title={`${slide.title} - 火狼动漫网`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/55 to-primary-950/20"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-8">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-bold mb-4">
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-fire-fill"></i>
                  </span>
                  {slide.tag}
                </span>
                <h2 className="text-3xl md:text-5xl font-bold text-background-50 leading-tight mb-4">
                  {slide.title}
                </h2>
                <p className="text-sm md:text-base text-background-200 leading-relaxed mb-6 wf-line-clamp-3">
                  {slide.summary}
                </p>
                <div className="flex items-center gap-3">
                  <Link
                    to={slide.link}
                    className="inline-flex items-center gap-1.5 px-5 h-11 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    查看详情
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </Link>
                  <Link
                    to="/anime"
                    className="inline-flex items-center gap-1.5 px-5 h-11 rounded-md bg-background-50/15 text-background-50 text-sm font-medium hover:bg-background-50/25 transition-colors whitespace-nowrap cursor-pointer border border-background-50/25"
                  >
                    浏览作品库
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(index - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer border border-background-50/25"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(index + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer border border-background-50/25"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/50 hover:bg-background-50/80"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}